import React, { useState } from 'react';
import { Input, Button, Tag, Spin, Modal, message, Tooltip, Badge } from 'antd';
import { PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, FilterOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined, CheckCircleOutlined, StopOutlined, EyeOutlined, UserOutlined } from '@ant-design/icons';
import { Drawer, List, Card, Typography } from 'antd';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { supplierService, Supplier, CreateSupplierData, UpdateSupplierData } from '../../services/supplierService';
import SupplierModalMobile from '../../components/suppliers/SupplierModalMobile';
import styles from './SuppliersPageMobile.module.css';

const { Text } = Typography;

const SuppliersPageMobile: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);

  const queryClient = useQueryClient();

  // Query danh sách nhà cung cấp
  const { data: suppliers = [], isLoading } = useQuery(
    ['suppliers', searchText],
    () => searchText ? supplierService.searchSuppliers(searchText) : supplierService.getSuppliers(),
    { staleTime: 5 * 60 * 1000 }
  );

  // Mutation xóa nhà cung cấp
  const deleteMutation = useMutation((id: number) => supplierService.deleteSupplier(id), {
    onSuccess: () => {
      message.success('Xóa nhà cung cấp thành công!');
      queryClient.invalidateQueries('suppliers');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Có lỗi xảy ra khi xóa nhà cung cấp');
    }
  });

  // Mutation tạo/cập nhật nhà cung cấp
  const createMutation = useMutation((data: CreateSupplierData) => supplierService.createSupplier(data), {
    onSuccess: () => {
      message.success('Thêm nhà cung cấp thành công!');
      queryClient.invalidateQueries('suppliers');
      setModalVisible(false);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Có lỗi xảy ra khi thêm nhà cung cấp');
    }
  });
  const updateMutation = useMutation(({ id, data }: { id: number; data: UpdateSupplierData }) => supplierService.updateSupplier(id, data), {
    onSuccess: () => {
      message.success('Cập nhật nhà cung cấp thành công!');
      queryClient.invalidateQueries('suppliers');
      setModalVisible(false);
      setEditingSupplier(null);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật nhà cung cấp');
    }
  });

  const handleAdd = () => {
    setEditingSupplier(null);
    setModalVisible(true);
  };
  const handleEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setModalVisible(true);
  };
  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn xóa nhà cung cấp này?',
      content: 'Hành động này không thể hoàn tác.',
      okText: 'Có, Xóa',
      okType: 'danger',
      cancelText: 'Không',
      onOk: () => deleteMutation.mutate(id),
    });
  };
  const handleView = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setDrawerVisible(true);
  };
  const handleSubmit = async (values: any) => {
    if (editingSupplier) {
      await updateMutation.mutateAsync({ id: editingSupplier.id, data: values });
    } else {
      await createMutation.mutateAsync(values);
    }
  };
  // Lọc dữ liệu chỉ theo searchText (bỏ lọc trạng thái)
  const filteredSuppliers = suppliers.filter((supplier: Supplier) => {
    return (
      !searchText ||
      supplier.name.toLowerCase().includes(searchText.toLowerCase()) ||
      supplier.code.toLowerCase().includes(searchText.toLowerCase())
    );
  });

  return (
    <div className={styles['supplier-mobile-container']}>
      {/* HEADER */}
      <header className={styles['supplier-mobile-header']}>
        {/* Thanh tìm kiếm */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <Input
            className={styles['supplier-mobile-search']}
            style={{ width: '100%', padding: '12px 40px 12px 16px', border: '1px solid #ced4da', borderRadius: 8, fontSize: 16, boxSizing: 'border-box' }}
            placeholder="Tìm kiếm nhà cung cấp..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <span style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: 18 }}>
            <ReloadOutlined />
          </span>
        </div>
        {/* Nút làm mới */}
        <div style={{ display: 'flex', gap: 12 }}>
          <Button
            style={{ flexGrow: 1, padding: 12, fontSize: 16, fontWeight: 600, borderRadius: 8, border: '1px solid #ced4da', color: '#212529', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            icon={<ReloadOutlined />}
            onClick={() => queryClient.invalidateQueries('suppliers')}
          >
            Làm mới
          </Button>
        </div>
        {/* Đã bỏ bộ lọc trạng thái */}
      </header>
      <Spin spinning={isLoading}>
        <div className={styles['supplier-mobile-list']}>
          {filteredSuppliers.map((item: Supplier) => (
            <div
              key={item.id}
              className={styles['supplier-mobile-card']}
              style={{
                maxWidth: 420,
                width: '100%',
                background: '#fff',
                borderRadius: 16,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                border: '1px solid #e5e7eb',
                margin: '0 auto 4px',
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#1890ff', textAlign: 'left' }}>{item.name}</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4, textAlign: 'left' }}>Mã NCC: {item.code}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      backgroundColor: item.is_active ? '#52c41a' : '#ff4d4f',
                      border: '2px solid #fff',
                      boxShadow: '0 0 0 1px #e5e7eb'
                    }} />
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 15, textAlign: 'left' }}>
                  {item.contact_person && <div><UserOutlined /> {item.contact_person}</div>}
                  {item.phone && <div><PhoneOutlined /> {item.phone}</div>}
                  {item.email && <div><MailOutlined /> {item.email}</div>}
                  {item.address && <div><EnvironmentOutlined /> {item.address}</div>}
                  <div style={{ fontSize: 12, color: '#888' }}>Ngày tạo: {dayjs(item.created_at).format('DD/MM/YYYY')}</div>
                </div>
              </div>
              <div style={{ background: '#f9fafb', padding: '12px 24px', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => handleEdit(item)} className={styles['supplier-mobile-edit-btn']} style={{ padding: 8, borderRadius: '50%', color: '#faad14', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                    <EditOutlined />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className={styles['supplier-mobile-delete-btn']} style={{ padding: 8, borderRadius: '50%', color: 'red', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                    <DeleteOutlined />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {filteredSuppliers.length === 0 && (
            <div className={styles['supplier-mobile-empty']}>Không có nhà cung cấp nào phù hợp.</div>
          )}
        </div>
      </Spin>
      {/* Modal thêm/sửa nhà cung cấp */}
      <SupplierModalMobile
        visible={modalVisible}
        supplier={editingSupplier || undefined}
        onSave={async (data: any) => {
          await handleSubmit(data);
        }}
        onCancel={() => {
          setModalVisible(false);
          setEditingSupplier(null);
        }}
        loading={createMutation.isLoading || updateMutation.isLoading}
      />
      {/* Floating Add Button */}
      <button
        onClick={handleAdd}
        className={styles['supplier-mobile-add-btn']}
        style={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: 100,
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: '#0d6efd',
          color: '#fff',
          border: 'none',
          boxShadow: '0 4px 16px rgba(13,110,253,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
          cursor: 'pointer',
          transition: 'background 0.2s',
        }}
        title="Thêm nhà cung cấp mới"
      >
        <PlusOutlined />
      </button>
      {/* Drawer chi tiết nhà cung cấp (có thể bổ sung sau) */}
      {/* <Drawer ... /> */}
    </div>
  );
};

export default SuppliersPageMobile;
